import { useEffect, useRef, useState } from 'react';
import type { MediaRef, Word } from '@ezishizi/content-schema';
import { loadLesson } from '../lib/content.ts';
import type { ChestPrize } from '../lib/chest.ts';
import { stickerWord } from '../lib/stickers.ts';
import { Chest } from './rewardIcons.tsx';
import { StickerArt } from './StickerArt.tsx';
import { Burst, Hanzi } from './ui.tsx';

/** How long the chest shakes before the lid goes up. */
const OPENING_MS = 650;

/**
 * The island chest: tap it, it shakes and opens, and the prize pops out. A bonus game comes with
 * a Play button (the reward is recorded when that game is finished, exactly as before); a shiny
 * sticker is recorded as the chest opens, with nothing more to play. A chest that was already
 * opened comes up open, showing the same prize again, so a reward is never lost.
 */
export function ChestOverlay({
  lessonId,
  setId,
  prize,
  done,
  media,
  onOpened,
  onPlay,
  onBook,
  onClose,
}: {
  lessonId: string;
  setId: string;
  prize: ChestPrize;
  /** The reward is already recorded as done. */
  done: boolean;
  /** The picture of the sticker prize, when it is one. */
  media: MediaRef | undefined;
  /** A sticker prize was just revealed: record the reward. */
  onOpened: () => void;
  onPlay: () => void;
  onBook: () => void;
  onClose: () => void;
}) {
  const [state, setState] = useState<'closed' | 'opening' | 'open'>(done ? 'open' : 'closed');
  const [word, setWord] = useState<Word | null>(null);
  const opened = useRef(done);

  const open = () => {
    if (state !== 'closed') return;
    setState('opening');
    setTimeout(() => {
      setState('open');
      if (prize.type === 'sticker' && !opened.current) {
        opened.current = true;
        onOpened();
      }
    }, OPENING_MS);
  };

  const char = prize.type === 'sticker' ? prize.char : null;
  useEffect(() => {
    if (!char || state !== 'open') return;
    let cancelled = false;
    void loadLesson(lessonId)
      .then((bundle) => {
        if (!cancelled) setWord(stickerWord(bundle, setId, char));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [char, state, lessonId, setId]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-ink/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Treasure chest"
      data-testid="chest-overlay"
      data-state={state}
      data-prize={prize.type}
    >
      <div className="pop relative flex max-h-full w-full max-w-md flex-col items-center gap-2 rounded-[32px] bg-paper p-5 text-center shadow-xl">
        <button
          type="button"
          className="btn btn-ghost absolute top-3 right-3 h-12 min-h-0 w-12 min-w-0 p-0 text-xl"
          aria-label="Close"
          data-testid="chest-close"
          onClick={onClose}
        >
          ✕
        </button>

        {state !== 'open' ? (
          <>
            <div className="display text-3xl text-brand">A treasure chest!</div>
            <button
              type="button"
              className="pressable relative w-56 landscape:w-40"
              aria-label="Open the chest"
              data-testid="chest-open"
              disabled={state === 'opening'}
              onClick={open}
            >
              <Chest state={state} />
            </button>
            <div className="text-lg text-muted">
              {state === 'closed' ? 'Tap to open it' : 'Here it comes…'}
            </div>
          </>
        ) : (
          <>
            <div className="relative w-44 landscape:w-32">
              <Chest state="open" />
              <Burst tone="accent" n={12} />
            </div>
            {prize.type === 'sticker' && media ? (
              <div
                className="chest-prize sticker-shiny flex w-full max-w-60 flex-col items-center gap-1 rounded-3xl p-3"
                data-testid="chest-prize-sticker"
              >
                <div className="text-sm font-bold tracking-widest text-accent-deep uppercase">
                  Shiny sticker!
                </div>
                <div className="h-24 w-24 landscape:h-16 landscape:w-16">
                  <StickerArt media={media} char={prize.char} />
                </div>
                <Hanzi className="text-4xl">{word?.text ?? prize.char}</Hanzi>
                <div className="min-h-[2.75rem] text-base text-muted">
                  {word && (
                    <>
                      <div>{word.pinyin}</div>
                      <div>{word.meaningEn}</div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="chest-prize flex flex-col items-center gap-1"
                data-testid="chest-prize-game"
              >
                <div className="display text-3xl text-brand">A bonus game!</div>
                <div className="text-lg text-muted">Play it to finish this island.</div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
              {prize.type === 'sticker' && media ? (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    data-testid="chest-book"
                    onClick={onBook}
                  >
                    Sticker book
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="chest-continue"
                    onClick={onClose}
                  >
                    Keep going
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="chest-play"
                  onClick={onPlay}
                >
                  Play ▶
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
